import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import HfReactHelper from 'common/utils/hf-react-helper';
import { getBrandName, SiteCodeType } from 'hello-react/common/brand';
import { unreachable } from 'hellospa/ts-utils';

import {
  WordmarkLogo,
  GlyphLogo,
  FaxGlyph,
  SignGlyph,
  FormsGlyph,
  FaxWordmark,
  SignWordmark,
  SignAPIWordmark,
  FormsWordmark,
  FssWordmark,
  FssGlyph,
  WordmarkLogoSize,
} from 'dig-components/logos';

import styles from './styles.scss';

const messages = defineMessages({
  altTextWordmark: {
    id: '',
    description: 'Screenreader helper text for the logo which takes users to the home page when pressed.',
    defaultMessage: '{brandName} logotype; Click to go home',
  },
  altTextGlyph: {
    id: '',
    description: 'Screenreader helper text for the brand logo which takes users to the home page when pressed.',
    defaultMessage: '{brandName} logo; Click to go home',
  },
  goHome: {
    id: '',
    description: 'Title text for a button which takes the user home when pressed.',
    defaultMessage: 'Go home',
  },
});

type LogoProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  siteCode: SiteCodeType,
  size?: WordmarkLogoSize,
  href?: string,
  variant?: 'api' | 'wordmark' | 'glyph', // default 'wordmark'
}

const Logo: React.FC<LogoProps> = ({
  siteCode,
  href,
  variant = 'wordmark',
  className = '',
  size = 32,
}) => {
  const intl = useIntl();
  const site = HfReactHelper.HfSites.getSite(siteCode);

  const wordmarkSrc = React.useMemo(() => {
    switch (siteCode) {
      case 'S': return SignWordmark;
      case 'F': return FaxWordmark;
      case 'W': return FormsWordmark;
      case 'D': return FssWordmark;
      default:
        return unreachable(siteCode);
    }
  }, [siteCode]);

  const glyphSrc = React.useMemo(() => {
    switch (siteCode) {
      case 'S': return SignGlyph;
      case 'F': return FaxGlyph;
      case 'W': return FormsGlyph;
      case 'D': return FssGlyph;
      default:
        return unreachable(siteCode);
    }
  }, [siteCode]);

  const altText = React.useMemo(() => {
    if (variant === 'glyph') {
      return intl.formatMessage(messages.altTextGlyph, {
        brandName: getBrandName(siteCode),
      });
    } else { // 'wordmark'
      return intl.formatMessage(messages.altTextWordmark, {
        brandName: getBrandName(siteCode),
      });
    }
  }, [intl, variant, siteCode]);

  return (
    <div
      className={className}
      // Use of literal "hellosign" here is acceptable. Questions? #ask-hs-frontend.
      // eslint-disable-next-line no-restricted-syntax
      data-qa-ref="hellosign-app-logo-container"
    >
      <a
        href={href ?? HfReactHelper.urlHelper('home/index', site)}
        title={intl.formatMessage(messages.goHome)}
        aria-label={altText}
        // Use of literal "hellosign" here is acceptable. Questions? #ask-hs-frontend.
        // eslint-disable-next-line no-restricted-syntax
        data-qa-ref="hellosign-app-logo"
      >
        {variant === 'api' && (
          <WordmarkLogo
            size={size}
            className={styles.logo}
            src={SignAPIWordmark} />
        )}
        {wordmarkSrc && variant === 'wordmark' && (
          <WordmarkLogo
            size={size}
            className={styles.logo}
            src={wordmarkSrc} />
        )}
        {glyphSrc && variant === 'glyph' && (
          <GlyphLogo
            size={size}
            className={styles.logo}
            src={glyphSrc} />
        )}
      </a>
    </div>
  );
};

export default Logo;
